import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Styles from './Step0BrandOverview.module.scss';
import {
  FiUpload,
  FiX,
  FiLink,
  FiCheck,
  FiInstagram,
  FiLinkedin,
  FiTwitter,
  FiYoutube,
  FiFacebook,
  FiFileText,
  FiZap,
  FiUsers,
  FiRefreshCw,
  FiGlobe,
  FiMic,
  FiTarget,
  FiHash,
  FiImage,
  FiMessageCircle,
} from 'react-icons/fi';
import { FaTiktok } from 'react-icons/fa';
import onBoardingService from 'services/onBoardingService';
import { postRequest, getRequest } from 'helpers/axios';
import { success as successToast, error as errorToast } from 'helpers/toaster';
import BrandKnowledgeAnimation from '../BrandKnowledgeAnimation';

const SOCIAL_PLATFORMS = [
  { id: 'twitter', name: 'Twitter/X', icon: FiTwitter, color: '#1DA1F2' },
  { id: 'linkedin', name: 'LinkedIn', icon: FiLinkedin, color: '#0A66C2' },
  { id: 'instagram', name: 'Instagram', icon: FiInstagram, color: '#E4405F' },
  { id: 'facebook', name: 'Facebook', icon: FiFacebook, color: '#1877F2', disabled: true },
  { id: 'youtube', name: 'YouTube', icon: FiYoutube, color: '#FF0000' },
  { id: 'tiktok', name: 'TikTok', icon: FaTiktok, color: '#000000' },
];

const Step0BrandOverview = ({
  data,
  onUpdate,
  onNext,
  onSaveAndExit,
  onBrandProfileCreated,
}) => {
  const [brandName, setBrandName] = useState(data.name || '');
  const [offering, setOffering] = useState(data.offering || '');
  const [websiteUrl, setWebsiteUrl] = useState(data.websiteUrl || '');
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    (data.socialChannels || []).map((ch) => ch.platform)
  );
  const [channelUrls, setChannelUrls] = useState(
    (data.socialChannels || []).reduce((acc, channel) => {
      // Try multiple sources for the URL
      const url = channel.url ||
        channel.platform_specific_settings?.profile_url ||
        channel.handle ||
        '';
      acc[channel.platform] = url;
      return acc;
    }, {})
  );
  const [uploadedFiles, setUploadedFiles] = useState(data.quickStartFiles || []);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [sourceStatuses, setSourceStatuses] = useState({});

  // Check if brand has been analyzed (has meaningful data)
  const isAnalyzed = !!(
    data.websiteAnalysisData ||
    (data.brandProfile?.industries?.length > 0) ||
    (data.brandProfile?.brand_tone?.length > 0) ||
    (data.contentThemes?.length > 0) ||
    data.valueProposition
  );

  // Get brand data from various sources
  const brandData = {
    name: data.name || data.brandProfile?.name || '',
    description: data.offering || data.brandProfile?.description || data.websiteAnalysisData?.brand_description || '',
    industries: data.brandProfile?.industries || data.websiteAnalysisData?.industries || [],
    brandTone: data.brandProfile?.brand_tone || data.websiteAnalysisData?.brand_tone || [],
    targetAudience: data.brandProfile?.target_audience || data.websiteAnalysisData?.target_audience || [],
    keyThemes: data.contentThemes || data.brandProfile?.content_themes || data.websiteAnalysisData?.detailed_topics || [],
    valueProposition: data.valueProposition || data.brandProfile?.value_proposition || data.websiteAnalysisData?.value_proposition || '',
  };

  // Calculate training progress
  const getTrainingProgress = () => {
    const modules = [
      {
        id: 'social',
        name: 'Social Channels',
        icon: FiUsers,
        isComplete: (data.socialChannels || []).some(ch =>
          ch.fetchedPosts?.length > 0 ||
          ch.fetchedTweets?.length > 0 ||
          ch.fetchedVideos?.length > 0 ||
          ch.platform_specific_settings?.frequency_stats
        ),
        status: (data.socialChannels || []).filter(ch =>
          ch.active ||
          ch.platform_specific_settings ||
          ch.fetchedPosts?.length > 0 ||
          ch.fetchedTweets?.length > 0 ||
          ch.fetchedVideos?.length > 0
        ).length + ' platforms connected',
      },
      {
        id: 'content',
        name: 'Content Samples',
        icon: FiFileText,
        isComplete: (data.contentSamples || []).length > 0,
        status: (data.contentSamples || []).length + ' samples',
      },
      {
        id: 'visual',
        name: 'Visual Identity',
        icon: FiImage,
        isComplete: !!(data.brandProfile?.brand_colors?.length > 0 || data.brandProfile?.logo_url),
        status: data.brandProfile?.brand_colors?.length > 0 ? 'Colors defined' : 'Not configured',
      },
      {
        id: 'audience',
        name: 'Audience',
        icon: FiTarget,
        isComplete: !!(data.brandProfile?.target_audience?.length > 0 || data.brandProfile?.customer_personas?.length > 0),
        status: data.brandProfile?.customer_personas?.length > 0
          ? data.brandProfile.customer_personas.length + ' personas'
          : 'Not configured',
      },
    ];
    return modules;
  };

  // Sync state when data prop changes (e.g., navigating back to this step)
  useEffect(() => {
    // Update brand name and offering if they changed
    if (data.name && data.name !== brandName) {
      setBrandName(data.name);
    }
    if (data.offering && data.offering !== offering) {
      setOffering(data.offering);
    }
    if (data.websiteUrl && data.websiteUrl !== websiteUrl) {
      setWebsiteUrl(data.websiteUrl);
    }

    // Update selected platforms and URLs from socialChannels
    if (data.socialChannels?.length > 0) {
      const platforms = data.socialChannels.map(ch => ch.platform);
      const urls = data.socialChannels.reduce((acc, channel) => {
        const url = channel.url ||
          channel.platform_specific_settings?.profile_url ||
          channel.handle ||
          '';
        if (url) {
          acc[channel.platform] = url;
        }
        return acc;
      }, {});

      // Only update if there are changes
      if (JSON.stringify(platforms.sort()) !== JSON.stringify(selectedPlatforms.sort())) {
        setSelectedPlatforms(platforms);
      }

      // Merge URLs - don't overwrite existing URLs with empty ones
      setChannelUrls(prev => {
        const merged = { ...prev };
        Object.keys(urls).forEach(platform => {
          if (urls[platform]) {
            merged[platform] = urls[platform];
          }
        });
        return merged;
      });
    }
  }, [data.socialChannels, data.name, data.offering, data.websiteUrl]);

  // Normalize URL
  const normalizeUrl = (url) => {
    if (!url) return '';
    let normalized = url.trim().toLowerCase();
    normalized = normalized.replace(/^["']|["']$/g, '');
    if (!normalized.match(/^https?:\/\//)) {
      normalized = `https://${normalized}`;
    }
    normalized = normalized.replace(/\/$/, '');
    return normalized;
  };

  // File upload dropzone
  const onDrop = useCallback((acceptedFiles) => {
    const filesWithMetadata = acceptedFiles.map((file) => ({
      file,
      content_type: 'brand_material',
      title: file.name,
    }));
    setUploadedFiles((prev) => [...prev, ...filesWithMetadata]);
    onUpdate({
      quickStartFiles: [...uploadedFiles, ...filesWithMetadata],
    });
  }, [uploadedFiles, onUpdate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'],
    },
    multiple: true,
  });

  const handleRemoveFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    onUpdate({ quickStartFiles: newFiles });
  };

  // Social platform toggle
  const togglePlatform = (platformId) => {
    if (selectedPlatforms.includes(platformId)) {
      setSelectedPlatforms(selectedPlatforms.filter((p) => p !== platformId));
      const newUrls = { ...channelUrls };
      delete newUrls[platformId];
      setChannelUrls(newUrls);
      onUpdate({
        socialChannels: (data.socialChannels || []).filter(
          (ch) => ch.platform !== platformId
        ),
      });
    } else {
      setSelectedPlatforms([...selectedPlatforms, platformId]);
      onUpdate({
        socialChannels: [
          ...(data.socialChannels || []),
          { platform: platformId, url: '', purpose: '' },
        ],
      });
    }
  };

  const handleUrlChange = (platformId, url) => {
    const newChannelUrls = { ...channelUrls, [platformId]: url };
    setChannelUrls(newChannelUrls);

    // Check if platform already exists in socialChannels
    const existingChannels = data.socialChannels || [];
    const platformExists = existingChannels.some(ch => ch.platform === platformId);

    if (platformExists) {
      // Update existing channel
      onUpdate({
        socialChannels: existingChannels.map((ch) =>
          ch.platform === platformId ? { ...ch, url } : ch
        ),
      });
    } else {
      // Add new channel with URL
      onUpdate({
        socialChannels: [
          ...existingChannels,
          { platform: platformId, url, purpose: '' },
        ],
      });
    }
  };

  // Extract Twitter username from URL or handle
  const extractTwitterUsername = (input) => {
    if (!input) return '';
    let cleaned = input.trim().replace(/^@/, '');
    if (cleaned.match(/twitter\.com|x\.com/)) {
      try {
        const urlObj = new URL(cleaned.startsWith('http') ? cleaned : `https://${cleaned}`);
        const username = urlObj.pathname.split('/')[1];
        return username || cleaned;
      } catch {
        return cleaned;
      }
    }
    return cleaned;
  };

  // Extract Instagram username from URL or handle
  const extractInstagramUsername = (input) => {
    if (!input) return '';
    let cleaned = input.trim().replace(/^@/, '');
    if (cleaned.match(/instagram\.com/)) {
      try {
        const urlObj = new URL(cleaned.startsWith('http') ? cleaned : `https://${cleaned}`);
        const pathParts = urlObj.pathname.split('/').filter(Boolean);
        // Skip reels/p/stories etc
        if (pathParts[0] && !['p', 'reel', 'reels', 'stories'].includes(pathParts[0])) {
          return pathParts[0];
        }
        return cleaned;
      } catch {
        return cleaned;
      }
    }
    return cleaned;
  };

  // Extract TikTok username from URL or handle
  const extractTiktokUsername = (input) => {
    if (!input) return '';
    let cleaned = input.trim().replace(/^@/, '');
    if (cleaned.match(/tiktok\.com/)) {
      try {
        const urlObj = new URL(cleaned.startsWith('http') ? cleaned : `https://${cleaned}`);
        const pathParts = urlObj.pathname.split('/').filter(Boolean);
        // Get username from @username path
        if (pathParts[0] && pathParts[0].startsWith('@')) {
          return pathParts[0].replace('@', '');
        }
        if (pathParts[0] && !['video', 'tag', 'music'].includes(pathParts[0])) {
          return pathParts[0];
        }
        return cleaned;
      } catch {
        return cleaned;
      }
    }
    return cleaned;
  };

  // Analyze brand with AI - triggers website AND social scraping
  const handleAnalyzeAndContinue = async () => {
    // VALIDATION: Require brand name and offering before proceeding
    if (!brandName.trim()) {
      errorToast('Please enter your brand name');
      return;
    }
    if (!offering.trim()) {
      errorToast('Please enter what you offer');
      return;
    }

    const hasWebsite = websiteUrl.trim();
    const twitterUrl = channelUrls['twitter'];
    const hasTwitter = selectedPlatforms.includes('twitter') && twitterUrl?.trim();
    const instagramUrl = channelUrls['instagram'];
    const hasInstagram = selectedPlatforms.includes('instagram') && instagramUrl?.trim();
    const linkedinUrl = channelUrls['linkedin'];
    const hasLinkedin = selectedPlatforms.includes('linkedin') && linkedinUrl?.trim();
    const youtubeUrl = channelUrls['youtube'];
    const hasYoutube = selectedPlatforms.includes('youtube') && youtubeUrl?.trim();
    const tiktokUrl = channelUrls['tiktok'];
    const hasTiktok = selectedPlatforms.includes('tiktok') && tiktokUrl?.trim();

    // Debug: Log what platforms and URLs we have
    console.log('Quick Start - Selected platforms:', selectedPlatforms);
    console.log('Quick Start - Channel URLs:', channelUrls);
    console.log('Quick Start - Will fetch:', {
      twitter: hasTwitter ? twitterUrl : 'NO',
      instagram: hasInstagram ? instagramUrl : 'NO',
      linkedin: hasLinkedin ? linkedinUrl : 'NO',
      youtube: hasYoutube ? youtubeUrl : 'NO',
      tiktok: hasTiktok ? tiktokUrl : 'NO',
    });

    // Save basic data first
    onUpdate({
      name: brandName,
      offering: offering,
      quickStartFiles: uploadedFiles,
    });

    setIsAnalyzing(true);
    let brandProfileId = null;
    let businessId = null;

    // Initialize source statuses based on what will be analyzed
    const initialStatuses = {};
    if (hasWebsite) initialStatuses.website = 'pending';
    if (hasTwitter) initialStatuses.twitter = 'pending';
    if (hasInstagram) initialStatuses.instagram = 'pending';
    if (hasLinkedin) initialStatuses.linkedin = 'pending';
    if (hasYoutube) initialStatuses.youtube = 'pending';
    if (hasTiktok) initialStatuses.tiktok = 'pending';
    if (uploadedFiles.length > 0) initialStatuses.files = 'pending';
    setSourceStatuses(initialStatuses);

    try {
      // 0. Initialize brand profile and social channels FIRST
      // This ensures we have a brand_profile_id before any fetches
      const hasSocialPlatforms = hasTwitter || hasInstagram || hasLinkedin || hasYoutube || hasTiktok;

      if (hasSocialPlatforms || brandName) {
        try {
          const initResponse = await postRequest('brand_intelligence/quick_start_init', {
            brand_name: brandName || 'Untitled Brand',
            platforms: selectedPlatforms.filter(p => channelUrls[p]?.trim()),
            channel_urls: channelUrls,
          });

          if (initResponse.data.success) {
            brandProfileId = initResponse.data.data.brand_profile.id;
            businessId = initResponse.data.data.brand_profile.business_id;

            // Update state with brand profile
            onUpdate({
              brandProfile: initResponse.data.data.brand_profile,
            });

            if (brandProfileId && onBrandProfileCreated) {
              onBrandProfileCreated(brandProfileId);
            }

            console.log('Quick Start initialized brand:', brandProfileId, 'business:', businessId);
          }
        } catch (initError) {
          console.error('Quick start init error:', initError);
          errorToast('Failed to create brand profile. Please try again.');
          setIsAnalyzing(false);
          return;
        }
      }

      // GATE: Must have brandId before proceeding
      if (!brandProfileId) {
        errorToast('Brand profile could not be created. Please try again.');
        setIsAnalyzing(false);
        return;
      }

      // 1. Website scraping (if URL provided)
      if (hasWebsite) {
        setSourceStatuses(prev => ({ ...prev, website: 'loading' }));
        const normalizedUrl = normalizeUrl(websiteUrl);
        const response = await onBoardingService.getAnalyzeBusiness(
          normalizedUrl,
          true,
          false, // forceRefresh
          businessId // Pass existing business ID to avoid duplicate creation
        );

        if (response.data.success && response.data.analysis) {
          const analysisData = response.data.analysis;
          const brandProfile = response.data.brand_profile;

          // Update brandProfileId if we got one from website analysis
          if (brandProfile?.id && !brandProfileId) {
            brandProfileId = brandProfile.id;
          }

          onUpdate({
            websiteUrl: normalizedUrl,
            websiteAnalysisData: analysisData,
            brandProfile: brandProfile,
            name: analysisData.brand_name || brandName || '',
            offering: analysisData.brand_description?.split('.')[0] || offering || '',
            marketPositioning: analysisData.core_messaging || data.marketPositioning || '',
            valueProposition: analysisData.value_proposition || data.valueProposition || '',
            uniqueSellingPoint: analysisData.value_proposition || data.uniqueSellingPoint || '',
            contentThemes: analysisData.detailed_topics || data.contentThemes || [],
          });

          if (brandProfile && brandProfile.id && onBrandProfileCreated) {
            onBrandProfileCreated(brandProfile.id);
          }

          // Toast removed - activity log shows progress
        } else {
          onUpdate({ websiteUrl: normalizedUrl });
          // Error shown in activity log
        }
        setSourceStatuses(prev => ({ ...prev, website: 'complete' }));
      }

      // 2. Twitter scraping (if selected with URL)
      if (hasTwitter) {
        setSourceStatuses(prev => ({ ...prev, twitter: 'loading' }));
        const username = extractTwitterUsername(twitterUrl);
        if (username) {
          try {
            const twitterResponse = await postRequest('social_posts/fetch_twitter', {
              username,
              limit: 20,
              exclude_retweets: true,
              exclude: ['retweets', 'replies'],
              brand_profile_id: brandProfileId,
            });

            if (twitterResponse.data.success) {
              const twitterData = twitterResponse.data.data;

              // Update social channels with fetched data
              let updatedChannels = (data.socialChannels || []).map((ch) => {
                if (ch.platform === 'twitter') {
                  return {
                    ...ch,
                    url: twitterUrl,
                    fetchedTweets: twitterData.tweets,
                    frequencyStats: twitterData.frequency_stats,
                    userProfile: twitterData.user_profile,
                    platform_specific_settings: {
                      user_profile: twitterData.user_profile,
                      frequency_stats: twitterData.frequency_stats,
                      fetched_posts: twitterData.tweets,
                    },
                  };
                }
                return ch;
              });

              onUpdate({
                socialChannels: updatedChannels,
                twitterData: twitterData, // Store for Social Channels tab
              });

              // Toast removed - activity log shows progress
              console.log(`Fetched ${twitterData.count} tweets from @${twitterData.username}`);
            }
          } catch (twitterError) {
            console.error('Twitter fetch error:', twitterError);
            // Don't block - continue anyway
          }
        }
        setSourceStatuses(prev => ({ ...prev, twitter: 'complete' }));
      }

      // 3. Instagram scraping (if selected with URL)
      if (hasInstagram) {
        setSourceStatuses(prev => ({ ...prev, instagram: 'loading' }));
        const username = extractInstagramUsername(instagramUrl);
        if (username) {
          try {
            const instagramResponse = await postRequest('social_posts/fetch_instagram', {
              username,
              limit: 20,
              analyze_content: true, // Enable caption/visual analysis
              analyze_videos: false, // Skip video transcription for Quick Start (too slow)
              brand_profile_id: brandProfileId,
            });

            if (instagramResponse.data.success) {
              const instagramData = instagramResponse.data.data;

              // Update social channels with fetched data
              let updatedChannels = (data.socialChannels || []).map((ch) => {
                if (ch.platform === 'instagram') {
                  return {
                    ...ch,
                    url: instagramUrl,
                    fetchedPosts: instagramData.posts,
                    frequencyStats: instagramData.frequency_stats,
                    userProfile: instagramData.profile,
                    contentAnalysis: instagramData.content_analysis,
                    platform_specific_settings: {
                      user_profile: instagramData.profile,
                      frequency_stats: instagramData.frequency_stats,
                      content_analysis: instagramData.content_analysis,
                      fetched_posts: instagramData.posts,
                    },
                  };
                }
                return ch;
              });

              onUpdate({
                socialChannels: updatedChannels,
                instagramData: instagramData, // Store for Social Channels tab
              });

              console.log(`Fetched ${instagramData.posts_count} posts from @${instagramData.profile.username}`);
            }
          } catch (instagramError) {
            console.error('Instagram fetch error:', instagramError);
            // Don't block - continue anyway
          }
        }
        setSourceStatuses(prev => ({ ...prev, instagram: 'complete' }));
      }

      // 4. LinkedIn scraping (if selected with URL)
      if (hasLinkedin) {
        setSourceStatuses(prev => ({ ...prev, linkedin: 'loading' }));
        // LinkedIn uses full profile URL, not username
        const profileUrl = linkedinUrl.trim();
        console.log('Quick Start - LinkedIn URL:', profileUrl, 'includes linkedin.com:', profileUrl.includes('linkedin.com'));
        if (profileUrl.includes('linkedin.com')) {
          try {
            console.log('Quick Start - Fetching LinkedIn with brand_profile_id:', brandProfileId);
            const linkedinResponse = await postRequest('social_posts/fetch_linkedin', {
              profile_url: profileUrl,
              limit: 20,
              brand_profile_id: brandProfileId,
            });
            console.log('Quick Start - LinkedIn response:', linkedinResponse.data.success, linkedinResponse.data.data?.posts_count);

            if (linkedinResponse.data.success) {
              const linkedinData = linkedinResponse.data.data;

              // Update social channels with fetched data
              let updatedChannels = (data.socialChannels || []).map((ch) => {
                if (ch.platform === 'linkedin') {
                  return {
                    ...ch,
                    url: profileUrl,
                    fetchedPosts: linkedinData.posts,
                    frequencyStats: linkedinData.frequency_stats,
                    userProfile: linkedinData.profile,
                    platform_specific_settings: {
                      user_profile: linkedinData.profile,
                      frequency_stats: linkedinData.frequency_stats,
                      fetched_posts: linkedinData.posts,
                    },
                  };
                }
                return ch;
              });

              onUpdate({
                socialChannels: updatedChannels,
                linkedinData: linkedinData, // Store for Social Channels tab
              });

              console.log(`Fetched ${linkedinData.posts_count} posts from ${linkedinData.profile.full_name}`);
            }
          } catch (linkedinError) {
            console.error('LinkedIn fetch error:', linkedinError);
            // Don't block - continue anyway
          }
        }
        setSourceStatuses(prev => ({ ...prev, linkedin: 'complete' }));
      }

      // 5. YouTube scraping (if selected with URL)
      if (hasYoutube) {
        setSourceStatuses(prev => ({ ...prev, youtube: 'loading' }));
        const channelUrl = youtubeUrl.trim();
        if (channelUrl) {
          try {
            const youtubeResponse = await postRequest('social_posts/fetch_youtube', {
              channel_url: channelUrl,
              limit: 20,
              brand_profile_id: brandProfileId,
            });

            if (youtubeResponse.data.success) {
              const youtubeData = youtubeResponse.data.data;

              // Update social channels with fetched data
              let updatedChannels = (data.socialChannels || []).map((ch) => {
                if (ch.platform === 'youtube') {
                  return {
                    ...ch,
                    url: channelUrl,
                    fetchedVideos: youtubeData.videos,
                    frequencyStats: youtubeData.frequency_stats,
                    channelData: youtubeData.channel,
                    platform_specific_settings: {
                      channel_data: youtubeData.channel,
                      frequency_stats: youtubeData.frequency_stats,
                      fetched_posts: youtubeData.videos,
                    },
                  };
                }
                return ch;
              });

              onUpdate({
                socialChannels: updatedChannels,
                youtubeData: youtubeData, // Store for Social Channels tab
              });

              console.log(`Fetched ${youtubeData.videos_count} videos from ${youtubeData.channel.title}`);
            }
          } catch (youtubeError) {
            console.error('YouTube fetch error:', youtubeError);
            // Don't block - continue anyway
          }
        }
        setSourceStatuses(prev => ({ ...prev, youtube: 'complete' }));
      }

      // 6. TikTok scraping (if selected with URL)
      if (hasTiktok) {
        setSourceStatuses(prev => ({ ...prev, tiktok: 'loading' }));
        const username = extractTiktokUsername(tiktokUrl);
        if (username) {
          try {
            const tiktokResponse = await postRequest('social_posts/fetch_tiktok', {
              username,
              limit: 20,
              brand_profile_id: brandProfileId,
            });

            if (tiktokResponse.data.success) {
              const tiktokData = tiktokResponse.data.data;

              // Update social channels with fetched data
              let updatedChannels = (data.socialChannels || []).map((ch) => {
                if (ch.platform === 'tiktok') {
                  return {
                    ...ch,
                    url: tiktokUrl,
                    fetchedVideos: tiktokData.videos,
                    frequencyStats: tiktokData.frequency_stats,
                    userProfile: tiktokData.profile,
                    platform_specific_settings: {
                      user_profile: tiktokData.profile,
                      frequency_stats: tiktokData.frequency_stats,
                      fetched_posts: tiktokData.videos,
                    },
                  };
                }
                return ch;
              });

              onUpdate({
                socialChannels: updatedChannels,
                tiktokData: tiktokData, // Store for Social Channels tab
              });

              console.log(`Fetched ${tiktokData.videos_count} videos from @${tiktokData.profile.username}`);
            }
          } catch (tiktokError) {
            console.error('TikTok fetch error:', tiktokError);
            // Don't block - continue anyway
          }
        }
        setSourceStatuses(prev => ({ ...prev, tiktok: 'complete' }));
      }

      // Handle files if any - extract content and run brand analysis
      if (uploadedFiles.length > 0 && brandProfileId) {
        try {
          setSourceStatuses(prev => ({ ...prev, files: 'analyzing' }));
          console.log(`Analyzing ${uploadedFiles.length} uploaded files...`);

          const fileResponse = await onBoardingService.analyzeFiles(brandProfileId, uploadedFiles);

          if (fileResponse.data.success) {
            const { analysis, files_processed, content_samples_created } = fileResponse.data.data;
            console.log(`File analysis complete: ${files_processed} files processed, ${content_samples_created} samples created`);

            // Update brand data with file analysis results (merge with website analysis if any)
            if (analysis) {
              onUpdate({
                brandDescription: analysis.brand_description || data.brandDescription,
                industry: analysis.industries?.[0] || data.industry,
                coreValues: analysis.tone || data.coreValues,
                valueProposition: analysis.value_proposition || data.valueProposition,
                targetAudience: analysis.target_audience || data.targetAudience,
                keywordsAndThemes: analysis.keywords_and_themes || data.keywordsAndThemes,
                contentTopics: analysis.detailed_topics || data.contentTopics,
              });
            }

            setSourceStatuses(prev => ({ ...prev, files: 'complete' }));
            successToast(`Analyzed ${files_processed} files successfully`);
          } else {
            console.error('File analysis failed:', fileResponse.data.error);
            setSourceStatuses(prev => ({ ...prev, files: 'error' }));
          }
        } catch (fileError) {
          console.error('File analysis error:', fileError);
          setSourceStatuses(prev => ({ ...prev, files: 'error' }));
          // Don't block - continue anyway
        }
      }

      // Reload brand data from backend to get contentSamples
      if (brandProfileId) {
        try {
          const reloadResponse = await getRequest(`brand_intelligence/profiles/${brandProfileId}`);
          if (reloadResponse.data.success) {
            const { content_samples, social_channels } = reloadResponse.data.data;

            // Update contentSamples so Step 6 can display fetched posts
            // Preserve the original URLs we entered (from channelUrls state)
            onUpdate({
              contentSamples: content_samples || [],
              socialChannels: (social_channels || []).map(channel => {
                // Preserve the URL from our local state if backend didn't have it
                const backendUrl = channel.platform_specific_settings?.profile_url;
                const localUrl = channelUrls[channel.platform];
                return {
                  platform: channel.platform,
                  url: backendUrl || localUrl || '',
                  handle: channel.handle,
                  platform_specific_settings: channel.platform_specific_settings
                };
              }),
            });

            console.log('Reloaded brand data with', content_samples?.length || 0, 'content samples');
          }
        } catch (reloadError) {
          console.error('Error reloading brand data:', reloadError);
        }
      }

      onNext();
    } catch (error) {
      console.error('Error analyzing brand:', error);
      onUpdate({ websiteUrl: normalizeUrl(websiteUrl) });
      errorToast('Analysis failed. You can retry on individual pages.');
      onNext();
    } finally {
      setIsAnalyzing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Handle re-analyze - switch back to Quick Start mode
  const handleReanalyze = () => {
    setShowQuickStart(true);
  };

  // Render Brand Overview Results View
  const renderResultsView = () => (
    <>
      {/* Header */}
      <div className={Styles.header}>
        <h2 className={Styles.title}>Brand Overview</h2>
        <p className={Styles.subtitle}>
          Your brand has been analyzed. Review your brand profile below or re-analyze to update.
        </p>
      </div>

      {/* Insights Dashboard */}
      <div className={Styles.insightsDashboard}>
        {/* Brand Snapshot Card */}
        <div className={Styles.snapshotCard}>
          <div className={Styles.snapshotHeader}>
            <div className={Styles.brandAvatar}>
              {brandData.name?.charAt(0)?.toUpperCase() || 'B'}
            </div>
            <div className={Styles.snapshotInfo}>
              <h3 className={Styles.brandName}>{brandData.name || 'Your Brand'}</h3>
              <p className={Styles.brandDescription}>
                {brandData.description || 'No description available'}
              </p>
            </div>
            <div className={Styles.analysisBadge}>
              <FiCheck /> Analyzed
            </div>
          </div>
        </div>

        {/* Insights Grid */}
        <div className={Styles.insightsGrid}>
          {/* Industries */}
          <div className={Styles.insightCard}>
            <div className={Styles.insightHeader}>
              <FiGlobe className={Styles.insightIcon} />
              INDUSTRIES
            </div>
            <div className={Styles.tagList}>
              {brandData.industries?.length > 0 ? (
                brandData.industries.map((industry, idx) => (
                  <span key={idx} className={Styles.tag}>{industry}</span>
                ))
              ) : (
                <span className={Styles.tag}>Not specified</span>
              )}
            </div>
          </div>

          {/* Brand Tone */}
          <div className={Styles.insightCard}>
            <div className={Styles.insightHeader}>
              <FiMic className={Styles.insightIcon} />
              BRAND TONE
            </div>
            <div className={Styles.tagList}>
              {brandData.brandTone?.length > 0 ? (
                brandData.brandTone.map((tone, idx) => (
                  <span key={idx} className={Styles.tag}>{tone}</span>
                ))
              ) : (
                <span className={Styles.tag}>Not specified</span>
              )}
            </div>
          </div>

          {/* Target Audience */}
          <div className={Styles.insightCard}>
            <div className={Styles.insightHeader}>
              <FiTarget className={Styles.insightIcon} />
              TARGET AUDIENCE
            </div>
            <div className={Styles.tagList}>
              {brandData.targetAudience?.length > 0 ? (
                (Array.isArray(brandData.targetAudience) ? brandData.targetAudience : [brandData.targetAudience]).map((audience, idx) => (
                  <span key={idx} className={Styles.tag}>{audience}</span>
                ))
              ) : (
                <span className={Styles.tag}>Not specified</span>
              )}
            </div>
          </div>

          {/* Key Themes */}
          <div className={Styles.insightCard}>
            <div className={Styles.insightHeader}>
              <FiHash className={Styles.insightIcon} />
              KEY THEMES
            </div>
            <div className={Styles.tagList}>
              {brandData.keyThemes?.length > 0 ? (
                brandData.keyThemes.slice(0, 6).map((theme, idx) => (
                  <span key={idx} className={Styles.tag}>{theme}</span>
                ))
              ) : (
                <span className={Styles.tag}>Not specified</span>
              )}
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        {brandData.valueProposition && (
          <div className={Styles.valuePropositionCard}>
            <div className={Styles.valuePropositionLabel}>VALUE PROPOSITION</div>
            <p className={Styles.valuePropositionText}>{brandData.valueProposition}</p>
          </div>
        )}

        {/* Training Progress */}
        <div className={Styles.trainingProgress}>
          <div className={Styles.trainingProgressHeader}>TRAINING PROGRESS</div>
          <div className={Styles.trainingModules}>
            {getTrainingProgress().map((module) => {
              const Icon = module.icon;
              return (
                <div key={module.id} className={Styles.moduleItem}>
                  <div className={Styles.moduleIcon}>
                    <Icon />
                  </div>
                  <div className={Styles.moduleInfo}>
                    <div className={Styles.moduleName}>{module.name}</div>
                    <div className={Styles.moduleStatus}>{module.status}</div>
                  </div>
                  {module.isComplete && <FiCheck className={Styles.moduleCheck} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Re-analyze Button */}
        <button
          type="button"
          className={Styles.reanalyzeBtn}
          onClick={handleReanalyze}
        >
          <FiRefreshCw />
          Re-analyze Brand
        </button>
      </div>

      {/* Footer Navigation */}
      <div className={Styles.footer}>
        <button
          type="button"
          className={Styles.saveExitBtn}
          onClick={onSaveAndExit}
        >
          Save & Exit
        </button>
        <div className={Styles.stepIndicator}>Step 1 of 7</div>
        <button
          type="button"
          className={Styles.analyzeBtn}
          onClick={onNext}
        >
          Continue
        </button>
      </div>
    </>
  );

  // Render Quick Start Input Form
  const renderQuickStartForm = () => (
    <>
      {/* Header */}
      <div className={Styles.header}>
        <h2 className={Styles.title}>Brand Overview</h2>
        <p className={Styles.subtitle}>
          Quick start by providing your website URL. Our AI will analyze and auto-populate your brand profile.
        </p>
      </div>

      {/* Quick Start Inputs */}
      <div className={Styles.formContent}>
        {/* Brand Name & Offering */}
        <div className={Styles.twoColumn}>
          <div className={Styles.inputGroup}>
            <label className={Styles.label}>
              Brand Name <span className={Styles.required}>*</span>
            </label>
            <input
              type="text"
              className={Styles.input}
              placeholder="Your brand or company name"
              value={brandName}
              onChange={(e) => {
                setBrandName(e.target.value);
                onUpdate({ name: e.target.value });
              }}
              disabled={isAnalyzing}
            />
          </div>
          <div className={Styles.inputGroup}>
            <label className={Styles.label}>
              What do you offer? <span className={Styles.required}>*</span>
            </label>
            <input
              type="text"
              className={Styles.input}
              placeholder="In one sentence, what does your company do?"
              value={offering}
              onChange={(e) => {
                setOffering(e.target.value);
                onUpdate({ offering: e.target.value });
              }}
              disabled={isAnalyzing}
            />
          </div>
        </div>

        {/* Website URL */}
        <div className={Styles.section}>
          <label className={Styles.sectionLabel}>
            <FiLink className={Styles.sectionIcon} />
            Website URL
          </label>
          <p className={Styles.helpText}>
            Enter your website and we'll analyze it to extract brand voice, messaging, and positioning
          </p>
          <div className={Styles.urlInputWrapper}>
            <input
              type="text"
              className={Styles.urlInput}
              placeholder="yourbrand.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              disabled={isAnalyzing}
            />
          </div>
        </div>

        {/* Social Channels Quick Connect */}
        <div className={Styles.section}>
          <label className={Styles.sectionLabel}>
            <FiUsers className={Styles.sectionIcon} />
            Social Channels (Optional)
          </label>
          <p className={Styles.helpText}>
            Select your active platforms - you can configure them in detail later
          </p>
          <div className={Styles.platformGrid}>
            {SOCIAL_PLATFORMS.map((platform) => {
              const Icon = platform.icon;
              const isSelected = selectedPlatforms.includes(platform.id);
              const isDisabled = platform.disabled;

              return (
                <div
                  key={platform.id}
                  className={`${Styles.platformCard} ${isSelected ? Styles.selected : ''} ${isDisabled ? Styles.disabled : ''}`}
                  onClick={() => !isAnalyzing && !isDisabled && togglePlatform(platform.id)}
                  style={{ '--platform-color': platform.color, ...(isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                >
                  <div className={Styles.platformContent}>
                    <div className={`${Styles.platformIcon} ${isSelected ? Styles.iconActive : ''}`}>
                      <Icon style={{ color: platform.color }} />
                    </div>
                    <span className={Styles.platformName}>
                      {platform.name}
                      {isDisabled && <span style={{ fontSize: '10px', display: 'block', color: '#888' }}>Coming Soon</span>}
                    </span>
                  </div>
                  {isSelected && (
                    <div className={Styles.selectedBadge}>
                      <FiCheck />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* URL inputs for selected platforms */}
          {selectedPlatforms.length > 0 && (
            <div className={Styles.platformUrls}>
              {selectedPlatforms.map((platformId) => {
                const platform = SOCIAL_PLATFORMS.find((p) => p.id === platformId);
                const Icon = platform?.icon;
                return (
                  <div key={platformId} className={Styles.platformUrlItem}>
                    <div className={Styles.platformUrlIcon}>
                      {Icon && <Icon style={{ color: platform?.color }} />}
                    </div>
                    <input
                      type="text"
                      className={Styles.platformUrlInput}
                      placeholder={`Your ${platform?.name} profile URL or @username`}
                      value={channelUrls[platformId] || ''}
                      onChange={(e) => handleUrlChange(platformId, e.target.value)}
                      disabled={isAnalyzing}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* File Upload */}
        <div className={Styles.section}>
          <label className={Styles.sectionLabel}>
            <FiUpload className={Styles.sectionIcon} />
            Brand Materials (Optional)
          </label>
          <p className={Styles.helpText}>
            Upload brand guidelines, content samples, or any documents that represent your brand
          </p>
          <div
            {...getRootProps()}
            className={`${Styles.dropzone} ${isDragActive ? Styles.dropzoneActive : ''}`}
          >
            <input {...getInputProps()} />
            <div className={Styles.dropzoneIcon}>
              <FiUpload />
            </div>
            <div className={Styles.dropzoneText}>
              <div className={Styles.dropzoneTitle}>
                {isDragActive ? 'Drop files here' : 'Click to upload or drag and drop'}
              </div>
              <div className={Styles.dropzoneSubtitle}>
                PDF, DOC, DOCX, TXT, Images (Max 10MB each)
              </div>
            </div>
          </div>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div className={Styles.filesList}>
              {uploadedFiles.map((fileObj, index) => (
                <div key={index} className={Styles.fileItem}>
                  <FiFileText className={Styles.fileIcon} />
                  <div className={Styles.fileInfo}>
                    <span className={Styles.fileName}>{fileObj.file?.name || fileObj.title}</span>
                    <span className={Styles.fileSize}>
                      {fileObj.file?.size ? formatFileSize(fileObj.file.size) : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={Styles.removeFileBtn}
                    onClick={() => handleRemoveFile(index)}
                    disabled={isAnalyzing}
                  >
                    <FiX />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>


        {/* Info Box */}
        {!isAnalyzing && (
          <div className={Styles.infoBox}>
            <FiZap className={Styles.infoIcon} />
            <div className={Styles.infoContent}>
              <div className={Styles.infoTitle}>AI-Powered Setup</div>
              <div className={Styles.infoText}>
                Click "Analyze & Continue" to scrape your website and auto-fill your brand profile.
                All fields will be populated automatically based on your website content.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <div className={Styles.footer}>
        <button
          type="button"
          className={Styles.saveExitBtn}
          onClick={onSaveAndExit}
          disabled={isAnalyzing}
        >
          Save & Exit
        </button>
        <div className={Styles.stepIndicator}>Step 1 of 7</div>
        <button
          type="button"
          className={Styles.analyzeBtn}
          onClick={handleAnalyzeAndContinue}
          disabled={isAnalyzing}
        >
          {isAnalyzing ? (
            <>
              <FiRefreshCw className={Styles.spinIcon} />
              Analyzing...
            </>
          ) : (
            <>
              <FiZap />
              Analyze & Continue
            </>
          )}
        </button>
      </div>
    </>
  );

  return (
    <div className={`${Styles.container} ${isAnalyzing ? Styles.analyzing : ''}`}>
      {isAnalyzed && !showQuickStart ? renderResultsView() : renderQuickStartForm()}

      {/* Analyzing Overlay - covers entire form */}
      {isAnalyzing && (
        <div className={Styles.analyzingOverlay}>
          <div className={Styles.analyzingCard}>
            <BrandKnowledgeAnimation
              sourceStatuses={sourceStatuses}
              title="Brand Knowledge Base"
              subtitle="Building your brand intelligence..."
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Step0BrandOverview;
